import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) localStorage.setItem("pc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("pc_token");
    setUser(false);
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  };

  const isReadOnly = !!user
    && user.role !== "team_leader"
    && (user.role === "manager" || (user.privileges || []).includes("read_only"));
  const isLeaderView = !!user && (user.role === "team_leader" || user.role === "manager");
  const hasMenuAccess = (menu) => {
    if (!user) return false;
    if (isLeaderView) return true;
    const privileges = user.privileges || [];
    return privileges.includes(`menu:${menu}`)
      || privileges.includes(`${menu}:write`)
      || (menu === "clients" && privileges.includes("menu:product-managers"));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshMe, isReadOnly, isLeaderView, hasMenuAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
